import type { Progress } from './progress';

const DAY_IN_MS = 86_400_000;
const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export type ActivityLevel = 0 | 1 | 2 | 3 | 4;

export type ActivityDay = {
  date: Date;
  dateKey: string;
  count: number;
  level: ActivityLevel;
  future: boolean;
};

export type ActivityWeek = {
  days: ActivityDay[];
};

export type ActivityMonthLabel = {
  label: string;
  startWeek: number;
  span: number;
};

export type ActivityCalendar = {
  weeks: ActivityWeek[];
  months: ActivityMonthLabel[];
  rangeLabel: string;
  activeDaysInRange: number;
  completionsInRange: number;
};

export type StreakStats = {
  current: number;
  longest: number;
  activeDays: number;
};

function atLocalNoon(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12);
}

function addLocalDays(value: Date, amount: number): Date {
  const next = atLocalNoon(value);
  next.setDate(next.getDate() + amount);
  return next;
}

function dateKeyParts(dateKey: string): [number, number, number] | null {
  const match = DATE_KEY_PATTERN.exec(dateKey);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const value = new Date(year, month - 1, day, 12);
  if (
    value.getFullYear() !== year ||
    value.getMonth() !== month - 1 ||
    value.getDate() !== day
  ) return null;
  return [year, month, day];
}

function dateKeyOrdinal(dateKey: string): number | null {
  const parts = dateKeyParts(dateKey);
  return parts ? Math.floor(Date.UTC(parts[0], parts[1] - 1, parts[2]) / DAY_IN_MS) : null;
}

export function localDateKey(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatActivityDate(dateKey: string): string {
  const parts = dateKeyParts(dateKey);
  if (!parts) return dateKey;
  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(new Date(parts[0], parts[1] - 1, parts[2], 12));
}

export function activityLevel(count: number): ActivityLevel {
  if (count <= 0) return 0;
  if (count === 1) return 1;
  if (count === 2) return 2;
  if (count === 3) return 3;
  return 4;
}

export function activityCountsFromProgress(
  progress: Pick<Progress, 'completedAtByDay'>,
  validDayIds?: ReadonlySet<string>
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const [dayId, timestamp] of Object.entries(progress.completedAtByDay)) {
    if (validDayIds && !validDayIds.has(dayId)) continue;
    const completedAt = new Date(timestamp);
    if (Number.isNaN(completedAt.getTime())) continue;
    const dateKey = localDateKey(completedAt);
    counts[dateKey] = (counts[dateKey] ?? 0) + 1;
  }
  return counts;
}

export function calculateStreaks(
  counts: Readonly<Record<string, number>>,
  today = new Date()
): StreakStats {
  const ordinals = [...new Set(
    Object.entries(counts)
      .filter(([, count]) => count > 0)
      .map(([dateKey]) => dateKeyOrdinal(dateKey))
      .filter((ordinal): ordinal is number => ordinal !== null)
  )].sort((left, right) => left - right);

  let longest = 0;
  let run = 0;
  let previous: number | null = null;
  for (const ordinal of ordinals) {
    run = previous !== null && ordinal === previous + 1 ? run + 1 : 1;
    longest = Math.max(longest, run);
    previous = ordinal;
  }

  const active = new Set(ordinals);
  const todayOrdinal = dateKeyOrdinal(localDateKey(today));
  let cursor = todayOrdinal;
  if (cursor !== null && !active.has(cursor) && active.has(cursor - 1)) cursor -= 1;

  let current = 0;
  while (cursor !== null && active.has(cursor)) {
    current += 1;
    cursor -= 1;
  }

  return { current, longest, activeDays: ordinals.length };
}

function monthLabels(weeks: ActivityWeek[]): ActivityMonthLabel[] {
  const starts: Array<{ label: string; startWeek: number; key: string }> = [];

  for (const [weekIndex, week] of weeks.entries()) {
    const firstOfMonth = week.days.find((day) => day.date.getDate() === 1);
    const labelDate = weekIndex === 0 ? week.days[0]?.date : firstOfMonth?.date;
    if (!labelDate) continue;
    const key = `${labelDate.getFullYear()}-${labelDate.getMonth()}`;
    if (starts.some((item) => item.key === key)) continue;
    const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(labelDate);
    const label = weekIndex === 0 || labelDate.getMonth() === 0
      ? `${month} ${labelDate.getFullYear()}`
      : month;
    starts.push({ label, startWeek: weekIndex, key });
  }

  return starts
    .map((item, index) => ({
      label: item.label,
      startWeek: item.startWeek,
      span: (starts[index + 1]?.startWeek ?? weeks.length) - item.startWeek
    }))
    .filter((item) => item.span >= 2);
}

export function buildActivityCalendar(
  counts: Readonly<Record<string, number>>,
  today = new Date(),
  weekCount = 52
): ActivityCalendar {
  const safeWeekCount = Math.max(1, Math.floor(weekCount));
  const todayAtNoon = atLocalNoon(today);
  const mondayIndex = (todayAtNoon.getDay() + 6) % 7;
  const currentMonday = addLocalDays(todayAtNoon, -mondayIndex);
  const firstMonday = addLocalDays(currentMonday, -(safeWeekCount - 1) * 7);
  const todayOrdinal = dateKeyOrdinal(localDateKey(todayAtNoon)) ?? Number.POSITIVE_INFINITY;

  const weeks = Array.from({ length: safeWeekCount }, (_, weekIndex): ActivityWeek => ({
    days: Array.from({ length: 7 }, (_, dayIndex): ActivityDay => {
      const date = addLocalDays(firstMonday, weekIndex * 7 + dayIndex);
      const dateKey = localDateKey(date);
      const count = Math.max(0, counts[dateKey] ?? 0);
      const ordinal = dateKeyOrdinal(dateKey) ?? Number.POSITIVE_INFINITY;
      return {
        date,
        dateKey,
        count,
        level: activityLevel(count),
        future: ordinal > todayOrdinal
      };
    })
  }));

  const days = weeks.flatMap((week) => week.days).filter((day) => !day.future);
  const firstDate = weeks[0]?.days[0]?.date ?? todayAtNoon;
  const rangeFormatter = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  return {
    weeks,
    months: monthLabels(weeks),
    rangeLabel: `${rangeFormatter.format(firstDate)} to ${rangeFormatter.format(todayAtNoon)}`,
    activeDaysInRange: days.filter((day) => day.count > 0).length,
    completionsInRange: days.reduce((total, day) => total + day.count, 0)
  };
}
